import {
  ArrowTrendingUpIcon,
  BellIcon,
  CalendarIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChatBubbleLeftRightIcon,
  Cog8ToothIcon,
  CursorArrowRaysIcon,
  FunnelIcon,
  HomeIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import { TFunction } from 'i18next';
import { eUserRole } from 'src/providers/UserProvider/User.enums';
import WapiIcon from '../../../public/svg/wapi-icon.svg';
import TikTokIcon from '../../../public/svg/tiktok-icon.svg';
import LeadbotIcon from '../../../public/svg/leadbot-icon.svg';
import { RouterPaths } from '../Router/Router.constants';

export function primaryMenuOptions(
  t: TFunction<'sidebar/sidebarComponent', undefined>,
  options: {
    canSeeWapiIntegration: boolean;
    canSeeTiktokIntegration: boolean;
    canSeeAlertCenter: boolean;
    canSeeAssistant?: boolean;
    assistantLabel?: string;
  },
) {
  const {
    canSeeWapiIntegration,
    canSeeTiktokIntegration,
    canSeeAlertCenter,
    canSeeAssistant = false,
  } = options;
  return [
    {
      icon: HomeIcon,
      label: t('page.routes.dashboard'),
      href: RouterPaths.dashboard,
      active: true,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN, eUserRole.SELLER],
    },
    {
      // AGI-284 — the Alert Center page (suggestions + news + alerts). All
      // three roles: the page itself gates each tab's content through the
      // alert engine's and the news registry's own role rules. Gated by the
      // per-workspace flag so the rollout is gradual, like the bell's own.
      //
      // AGI-346 — deliberately SECTIONLESS, so it joins Dashboard in the
      // unlabeled group that renders first. It sat under `tools` beside
      // Leadclick and Analitica, which reads as one more tool to go operate;
      // it is where the user is SENT when something needs them, which is the
      // same standing as Dashboard, not a peer of the tools.
      icon: BellIcon,
      label: t('page.routes.alertCenter'),
      href: RouterPaths.alertCenter,
      active: canSeeAlertCenter,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN, eUserRole.SELLER],
    },
    {
      icon: FunnelIcon,
      label: t('page.routes.funnels'),
      href: RouterPaths.funnels,
      section: 'crm',
      active: true,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN, eUserRole.SELLER],
    },
    {
      icon: ChatBubbleOvalLeftEllipsisIcon,
      label: t('page.routes.chat'),
      href: RouterPaths.chat,
      section: 'crm',
      active: true,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN, eUserRole.SELLER],
    },
    {
      icon: CalendarIcon,
      label: t('page.routes.agenda'),
      href: RouterPaths.agenda,
      section: 'crm',
      active: true,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN, eUserRole.SELLER],
    },
    {
      icon: IdentificationIcon,
      label: t('page.routes.directory'),
      href: RouterPaths.directory,
      section: 'crm',
      active: true,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN, eUserRole.SELLER],
    },
    {
      icon: ChatBubbleLeftRightIcon,
      label: options.assistantLabel ?? t('page.routes.assistant'),
      href: RouterPaths.assistant,
      section: 'tools',
      active: canSeeAssistant,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN, eUserRole.SELLER],
    },
    {
      icon: LeadbotIcon,
      label: t('page.routes.leadbot'),
      href: RouterPaths.leadbot,
      section: 'tools',
      active: true,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN],
    },
    {
      icon: CursorArrowRaysIcon,
      label: t('page.routes.leadclick'),
      href: RouterPaths.leadclick,
      section: 'tools',
      active: true,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN],
    },
    {
      icon: ArrowTrendingUpIcon,
      label: t('page.routes.analytics'),
      href: RouterPaths.analytics,
      section: 'tools',
      active: true,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN, eUserRole.SELLER],
    },
    {
      icon: WapiIcon,
      label: t('page.routes.wapi'),
      href: RouterPaths.wapi,
      section: 'channels',
      active: canSeeWapiIntegration,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN, eUserRole.SELLER],
    },
    {
      icon: TikTokIcon,
      label: t('page.routes.tiktok'),
      href: RouterPaths.tiktok,
      section: 'channels',
      active: canSeeTiktokIntegration,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN],
    },
  ];
}

export function secondaryMenuOptions(
  t: TFunction<'sidebar/sidebarComponent', undefined>,
) {
  return [
    {
      icon: Cog8ToothIcon,
      label: t('page.routes.settings'),
      href: RouterPaths.settings,
      userRole: [eUserRole.OWNER, eUserRole.ADMIN],
    },
  ];
}
